import type { Client } from '@openphone/internal-api-client'
import { startDurationVital, stopDurationVital } from '@openphone/quo-observability'
import type { FormattedExecutionResult, Sink } from 'graphql-ws'
import type { GraphQLResponse } from 'relay-runtime'
import { Network, Observable } from 'relay-runtime'

import { createGraphqlSubscriptionClient } from './createGraphqlSubscriptionClient'
import { surfaceRouterErrors } from './surfaceRouterErrors'

// Functional network performance tracker
function createNetworkPerformanceTracker() {
  const activeOperations = new Map<string, { startTime: number }>()

  return {
    startOperation(operationId: string, operationName: string, operationKind: string) {
      const startTime = performance.now()
      activeOperations.set(operationId, { startTime })

      startDurationVital({
        name: 'relay-network-operation',
        options: {
          description: `GraphQL ${operationKind}: ${operationName}`,
          context: {
            operationName,
            operationType: operationKind,
            networkRequestId: operationId,
          },
        },
      })
    },

    completeOperation(operationId: string, _operationName: string, _success: boolean) {
      const operation = activeOperations.get(operationId)
      if (!operation) {
        return
      }

      stopDurationVital('relay-network-operation')
      activeOperations.delete(operationId)
    },
  }
}

interface CreateNetworkOptions {
  httpClient: Client
  onReconnect?: () => void
}

export function createNetworkWithPerformanceTracking({
  httpClient,
  onReconnect,
}: CreateNetworkOptions) {
  const networkTracker = createNetworkPerformanceTracker()
  const urlBuilder = new URL('/api/graphql', window.location.origin)
  urlBuilder.searchParams.set('x-op-env', httpClient.getEnv())
  const backendPreview = httpClient.getBackendPreview()

  if (backendPreview) {
    urlBuilder.searchParams.set('x-op-backend-preview', backendPreview)
  }

  const url = urlBuilder.toString()

  const wsClient = createGraphqlSubscriptionClient({
    url,
    onReconnect,
    connectionParams: async () => {
      const headers = await httpClient.getHeadersForUrl(url)
      return headers
    },
  })

  return Network.create(
    (operation, variables) => {
      return Observable.create((sink) => {
        const operationId = `${operation.name}-${Date.now()}-${Math.random()}`
        const operationName = operation.name
        const operationKind = operation.operationKind

        // Start performance tracking. Variables stay out: they carry user input.
        networkTracker.startOperation(operationId, operationName, operationKind)

        httpClient
          .post(new URL('/api/graphql', window.location.origin).toString(), {
            query: operation.text,
            variables,
          })
          .then((response: GraphQLResponse) => {
            // Complete performance tracking - success
            networkTracker.completeOperation(operationId, operationName, true)

            sink.next(surfaceRouterErrors(response))
            sink.complete()
          })
          .catch((error: Error) => {
            // Complete performance tracking - error
            networkTracker.completeOperation(operationId, operationName, false)

            sink.error(error)
          })
      })
    },
    (operation, variables) => {
      return Observable.create((sink) => {
        const wsSink = sink as Sink<
          FormattedExecutionResult<Record<string, unknown>, unknown>
        >
        return wsClient.subscribe(
          {
            operationName: operation.name,
            query: operation.text ?? '',
            variables: variables,
          },
          {
            next: (value) => wsSink.next(surfaceRouterErrors(value)),
            error: (error) => wsSink.error(error),
            complete: () => wsSink.complete(),
          },
        )
      })
    },
  )
}
